import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import PageLayout from '../../components/layouts/PageLayout';
import {
  AcknowledgementAuditTrailDetailModal,
  AcknowledgementAuditTrailFilterBar,
  AcknowledgementAuditTrailSummaryCards,
  AcknowledgementAuditTrailTable,
} from '../../components/acknowledgementAuditTrail';
import { authService } from '../../auth/authService';
import { AUDIT_TRAIL_INFO_BANNER } from '../../constants/acknowledgementAuditTrail.constants';
import { useAcknowledgementAuditTrailFilters } from '../../hooks/useAcknowledgementAuditTrailFilters';
import { useAcknowledgementAuditTrailExport } from '../../hooks/useAcknowledgementAuditTrailExport';
import {
  buildReferringClinicianOptions,
  useCriticalResultsDefaultFacilityId,
  useCriticalResultsFacilities,
  useCriticalResultsNotifyWarning,
  useCriticalResultsSuperAdmin,
} from '../../hooks/useCriticalResultsPageSetup';
import {
  countAcknowledgementAuditTrailEvents,
  searchAcknowledgementAuditTrail,
} from '../../services/acknowledgementAuditTrailService';
import type {
  AuditTrailDetailContext,
  AuditTrailExportFormat,
  AuditTrailListItem,
  AuditTrailSearchMeta,
  AuditTrailSummaryStats,
} from '../../types/acknowledgementAuditTrail.types';
import {
  buildDetailContextFromListItem,
  computeSummaryStats,
} from '../../utils/acknowledgementAuditTrailUtils';
import {
  canAccessAcknowledgementAuditTrail,
  canExportAcknowledgementAuditTrail,
} from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import { ICON_STROKE_WIDTH } from '../../constants';
import './CriticalResultsDashboard.css';
import './AcknowledgementAuditTrail.css';

const breadcrumbs = [
  { label: 'Home', path: '/' },
  { label: 'Dashboard', path: '/admin-dashboard' },
  { label: 'Analytics' },
  { label: 'Ack Audit Trail' },
];

export default function AcknowledgementAuditTrail() {
  const user = authService.getUser();
  const isSuperAdmin = useCriticalResultsSuperAdmin();
  const canExport = canExportAcknowledgementAuditTrail(user);
  const { uiNotificationService } = useCriticalResultsNotifyWarning();
  const {
    exporting,
    exportMenuOpen,
    toggleExportMenu,
    handleExport: runExport,
  } = useAcknowledgementAuditTrailExport(uiNotificationService);
  const defaultFacilityId = useCriticalResultsDefaultFacilityId();
  const { filters, listState, apiFilters, pageSize, updateFilters, updateListState, clearFilters } =
    useAcknowledgementAuditTrailFilters(defaultFacilityId);

  const [rows, setRows] = useState<AuditTrailListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchMeta, setSearchMeta] = useState<AuditTrailSearchMeta | null>(null);
  const [summaryRows, setSummaryRows] = useState<AuditTrailListItem[]>([]);
  const [auditEventTotal, setAuditEventTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const [detailContext, setDetailContext] = useState<AuditTrailDetailContext | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const { facilitySelectOptions } = useCriticalResultsFacilities(
    isSuperAdmin,
    filters.facilityId,
    value => updateFilters({ facilityId: value }, { resetPage: true })
  );

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchDraft !== filters.search) {
        updateFilters({ search: searchDraft }, { resetPage: true });
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchDraft, filters.search, updateFilters]);

  const loadRows = useCallback(async () => {
    if (!apiFilters) {
      return;
    }
    setLoading(true);
    setError(null);

    const [listResult, summaryResult] = await Promise.all([
      searchAcknowledgementAuditTrail(apiFilters),
      searchAcknowledgementAuditTrail({
        ...apiFilters,
        limit: 200,
        offset: 0,
      }),
    ]);

    if (!listResult.success) {
      setRows([]);
      setTotalCount(0);
      setSummaryRows([]);
      setAuditEventTotal(0);
      setError(listResult.error || 'Failed to load acknowledgement audit records');
      setLoading(false);
      return;
    }

    const enrichedRows = await Promise.all(
      listResult.data.map(async row => {
        const eventCount = await countAcknowledgementAuditTrailEvents(row.id, row.facilityId);
        return { ...row, eventCount };
      })
    );

    let eventsTotal = 0;
    const eventCounts = await Promise.all(
      (summaryResult.success ? summaryResult.data : [])
        .slice(0, 50)
        .map(row => countAcknowledgementAuditTrailEvents(row.id, row.facilityId))
    );
    eventsTotal = eventCounts.reduce((sum, count) => sum + (count ?? 0), 0);

    setRows(enrichedRows);
    setTotalCount(listResult.count);
    setSearchMeta(listResult.meta ?? null);
    setSummaryRows(summaryResult.success ? summaryResult.data : listResult.data);
    setAuditEventTotal(eventsTotal);
    setLoading(false);
  }, [apiFilters]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const summaryStats = useMemo<AuditTrailSummaryStats>(
    () => computeSummaryStats(summaryRows, totalCount, auditEventTotal),
    [summaryRows, totalCount, auditEventTotal]
  );

  const clinicianOptions = useMemo(
    () => buildReferringClinicianOptions(searchMeta?.referringClinicianSummary, filters.clinician),
    [searchMeta?.referringClinicianSummary, filters.clinician]
  );

  const handleRowSelect = useCallback((row: AuditTrailListItem) => {
    setDetailContext(buildDetailContextFromListItem(row));
    setDetailOpen(true);
  }, []);

  const handleDetailClose = useCallback(() => {
    setDetailOpen(false);
    setDetailContext(null);
  }, []);

  const onExport = useCallback(
    (format: AuditTrailExportFormat) => {
      if (!apiFilters) {
        return;
      }
      void runExport(format, apiFilters);
    },
    [apiFilters, runExport]
  );

  if (!canAccessAcknowledgementAuditTrail(user)) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  if (!defaultFacilityId && !isSuperAdmin) {
    return (
      <PageLayout
        title="Ack Audit Trail"
        breadcrumbs={breadcrumbs}
      >
        <p className="crd-message">Your account is not associated with a facility.</p>
      </PageLayout>
    );
  }

  if (!filters.facilityId) {
    return (
      <PageLayout
        title="Ack Audit Trail"
        breadcrumbs={breadcrumbs}
      >
        <p className="crd-message">
          {isSuperAdmin
            ? 'Select a facility to view the acknowledgement audit trail.'
            : 'Your account is not associated with a facility.'}
        </p>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Ack Audit Trail"
      hideTitleInHeader
      breadcrumbs={breadcrumbs}
    >
      <div className="crd-dashboard aat-dashboard">
        <div
          className="aat-info-banner"
          role="note"
        >
          <Info
            size={18}
            strokeWidth={ICON_STROKE_WIDTH}
            aria-hidden
          />
          <div>
            <strong>Read-only audit trail</strong>
            {AUDIT_TRAIL_INFO_BANNER}
          </div>
        </div>

        <AcknowledgementAuditTrailFilterBar
          startDate={filters.startDate}
          endDate={filters.endDate}
          onStartDateChange={value => updateFilters({ startDate: value }, { resetPage: true })}
          onEndDateChange={value => updateFilters({ endDate: value }, { resetPage: true })}
          facilityOptions={facilitySelectOptions}
          facilityId={filters.facilityId}
          onFacilityChange={value => updateFilters({ facilityId: value }, { resetPage: true })}
          showFacilityFilter={isSuperAdmin}
          criticalityBand={filters.criticalityBand}
          onCriticalityBandChange={value =>
            updateFilters({ criticalityBand: value }, { resetPage: true })
          }
          acknowledgementStatus={filters.acknowledgementStatus}
          onAcknowledgementStatusChange={value =>
            updateFilters({ acknowledgementStatus: value }, { resetPage: true })
          }
          clinician={filters.clinician}
          onClinicianChange={value => updateFilters({ clinician: value }, { resetPage: true })}
          clinicianOptions={clinicianOptions}
          search={searchDraft}
          onSearchChange={setSearchDraft}
          onClear={clearFilters}
          loading={loading}
          showExport={canExport}
          exporting={exporting}
          exportMenuOpen={exportMenuOpen}
          onExportMenuToggle={toggleExportMenu}
          onExport={onExport}
        />

        <AcknowledgementAuditTrailSummaryCards
          stats={summaryStats}
          loading={loading}
        />

        <AcknowledgementAuditTrailTable
          rows={rows}
          totalCount={totalCount}
          currentPage={listState.page}
          pageSize={pageSize}
          loading={loading}
          error={error}
          onPageChange={page => updateListState({ page })}
          onRowSelect={handleRowSelect}
        />

        <AcknowledgementAuditTrailDetailModal
          open={detailOpen}
          context={detailContext}
          onClose={handleDetailClose}
        />
      </div>
    </PageLayout>
  );
}
